import React from 'react';
import PropTypes from 'prop-types';
import { FlexGrid, Loader } from '@gasbuddy/react-components';
import { isEmail } from 'react-multi-email';

import Notification from '../../notification';
import Modal from '../../modal';
import InputWithHeader from '../../input-with-header';

import styles from './styles.css';

const { api } = require('../../../../lib/bputils').default;


export default function ExportReviewsModal({ show, stationListId, startDate, endDate, searchTerm, filters, sortOrder, onClose }) {
  const [exportEmail, setExportEmail] = React.useState('');
  const [exportEmailError, setExportEmailError] = React.useState('');
  const [sendingExportRequest, setSendingExportRequest] = React.useState(false);

  const handleOnExportEmailChange = ({ target: { value } }) => {
    setExportEmail(value);
  };

  const validateEmail = () => {
    if (exportEmail.length) {
      const isValidEmail = isEmail(exportEmail);
      if (exportEmailError.length && isValidEmail) {
        setExportEmailError('');
      } else if (!exportEmailError.length && !isValidEmail) {
        setExportEmailError('Not a valid email');
        return false;
      }
    } else {
      setExportEmailError('Please enter an email');
      return false;
    }
    return true;
  };

  const getReviewPostBody = () => ({
    filters,
    limit: 10000,
    offset: 0,
    search_term: searchTerm,
    sort_direction: sortOrder,
    sort_field: 'create_date',
  });

  const handleOnExportClick = async () => {
    if (!validateEmail()) {
      return false;
    }
    setSendingExportRequest(true);
    const params = `start_date=${startDate}&end_date=${endDate}`;
    const postBody = getReviewPostBody();

    await api.post({
      path: `/export/reviews/station-lists/${stationListId}?${params}`,
      data: {
        email: exportEmail,
        ...postBody,
      },
      error: () => {
        Notification.error('Unable to complete your export request');
        setSendingExportRequest(false);
      },
      success: () => {
        Notification.success('Export request sent successfully');
        setSendingExportRequest(false);
        onClose();
      },
    });
    return true;
  };

  const handleOnModalClose = () => {
    onClose();
    setExportEmail('');
    setExportEmailError('');
    setSendingExportRequest(false);
  };

  const handleOnKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleOnExportClick();
    }
  };

  return (
    <Modal
      onClose={handleOnModalClose}
      title="We are getting your data ready"
      show={show}
      background
      onSave={handleOnExportClick}
      saveButtonText={sendingExportRequest ? <Loader inverse size="xs" className={styles.exportCTALoader} /> : 'Export'}
    >
      <FlexGrid className={styles.paddingBottom}>
        <FlexGrid.Column className={`${styles.exportModalText} ${styles.paddingBottom}`}>
          <p>
            {`We're just getting things ready but first, let us know which email you want your data sent to,
              and we'll ship it off to you when it's ready!`}
          </p>
          <p>
            <b>
                This export will contain a maximum of 10,000 reviews.
                Please contact
              {' '}
              <a href="mailto:pages@gasbuddy.com">pages@gasbuddy.com</a>
                if you require a full export of reviews for this timeframe.
            </b>
          </p>
        </FlexGrid.Column>
        <FlexGrid.Column>
          <InputWithHeader
            label="Recipient"
            placeholder="Email"
            value={exportEmail}
            onChange={handleOnExportEmailChange}
            onBlur={validateEmail}
            onKeyDown={handleOnKeyDown}
            error={exportEmailError}
            required
          />
          {exportEmailError && <span className={styles.errorMessage}>{exportEmailError}</span>}
        </FlexGrid.Column>
      </FlexGrid>
    </Modal>
  );
}

ExportReviewsModal.propTypes = {
  show: PropTypes.bool,
  stationListId: PropTypes.number.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  searchTerm: PropTypes.string,
  sortOrder: PropTypes.oneOf(['desc', 'asc']),
  filters: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func.isRequired,
};

ExportReviewsModal.defaultProps = {
  show: false,
  searchTerm: '',
  sortOrder: 'desc',
  filters: [],
};
